import { useState, useCallback, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../lib/translations';
import { calculateTotal } from '../lib/pricingLogic';
import { Check } from 'lucide-react';
import Step1AccountingForm from './steps/Step1AccountingForm';
import Step2Documents from './steps/Step2Documents';
import Step3Employees from './steps/Step3Employees';
import Step4Specifics from './steps/Step4Specifics';
import Step5Results from './steps/Step5Results';

const STEPS = ['step1', 'step2', 'step3', 'step4', 'step5'];
const STORAGE_KEY = 'kurdynowski_calculator';

const INITIAL_STATE = {
  accountingForm: '',
  legalEntity: '',
  vatPayer: null,
  vatEu: false,
  documentCount: 20,
  employeeCount: 0,
  zusOwner: false,
  partnerCount: 0,
  zusPersonsCount: 0,
  businessSpecifics: [],
  additionalServices: [],
};

function loadSavedState() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return { formData: { ...INITIAL_STATE, ...parsed.formData }, step: parsed.step || 0 };
    }
  } catch { /* ignore */ }
  return null;
}

export default function Calculator() {
  const { language } = useLanguage();
  const saved = useRef(loadSavedState());
  const [step, setStep] = useState(saved.current?.step || 0);
  const [formData, setFormData] = useState(saved.current?.formData || INITIAL_STATE);
  const [direction, setDirection] = useState('right');

  // Save to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ formData, step }));
  }, [formData, step]);

  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const goNext = useCallback(() => {
    setDirection('right');
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  }, []);

  const goBack = useCallback(() => {
    setDirection('left');
    setStep(s => Math.max(s - 1, 0));
  }, []);

  const resetCalculator = useCallback(() => {
    setFormData(INITIAL_STATE);
    setStep(0);
    setDirection('right');
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const totals = calculateTotal(formData);

  const canProceed = () => {
    if (step === 0) return formData.accountingForm && formData.legalEntity && formData.vatPayer !== null;
    if (step === 1) return formData.documentCount > 0;
    return true;
  };

  const stepProgress = ((step) / (STEPS.length - 1)) * 100;
  const animClass = direction === 'right' ? 'animate-slide-right' : 'animate-slide-left';

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="calculator-container">
      {/* Stepper */}
      <div className="mb-10 sm:mb-14" data-testid="stepper">
        <div className="relative flex items-start justify-between">
          <div className="absolute top-5 left-0 w-full h-0.5 bg-slate-200" />
          <div
            className="absolute top-5 left-0 h-0.5 bg-[#1B3A7B] transition-all duration-500 ease-out"
            style={{ width: `${stepProgress}%` }}
          />

          {STEPS.map((s, i) => (
            <div
              key={s}
              className="relative z-10 flex flex-col items-center gap-2 w-20"
              data-testid={`stepper-step-${i}`}
            >
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm border-2 transition-all duration-300 ${
                  i < step
                    ? 'bg-[#1B3A7B] border-[#1B3A7B] text-white'
                    : i === step
                    ? 'bg-[#1B3A7B] border-[#1B3A7B] text-white shadow-lg shadow-blue-900/20'
                    : 'bg-white border-slate-300 text-slate-400'
                }`}
              >
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              <span
                className={`text-xs text-center leading-tight hidden sm:block ${
                  i <= step ? 'text-[#1B3A7B] font-medium' : 'text-slate-400'
                }`}
              >
                {t(`steps.${s}`, language)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Step Content with directional animation */}
      <div className={animClass} key={step}>
        {step === 0 && <Step1AccountingForm formData={formData} updateField={updateField} />}
        {step === 1 && <Step2Documents formData={formData} updateField={updateField} />}
        {step === 2 && <Step3Employees formData={formData} updateField={updateField} />}
        {step === 3 && <Step4Specifics formData={formData} updateField={updateField} />}
        {step === 4 && (
          <Step5Results formData={formData} totals={totals} onReset={resetCalculator} onBack={() => { setDirection('left'); setStep(0); }} />
        )}
      </div>

      {/* Navigation */}
      {step < 4 && (
        <div className="flex justify-between mt-8 sm:mt-10" data-testid="step-navigation">
          <button
            onClick={goBack}
            disabled={step === 0}
            className={`px-6 py-3 rounded-md text-base font-medium transition-all ${
              step === 0
                ? 'text-slate-300 cursor-not-allowed'
                : 'bg-white border-2 border-[#1B3A7B] text-[#1B3A7B] hover:bg-[#1B3A7B] hover:text-white'
            }`}
            data-testid="back-btn"
          >
            {t('common.back', language)}
          </button>

          {step < 3 ? (
            <button
              onClick={goNext}
              disabled={!canProceed()}
              className={`px-8 py-3 rounded-md text-base font-medium transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 ${
                canProceed()
                  ? 'bg-[#E8941A] hover:bg-[#d68515] text-white'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none hover:shadow-none hover:translate-y-0'
              }`}
              data-testid="next-btn"
            >
              {t('common.next', language)} →
            </button>
          ) : (
            <button
              onClick={goNext}
              className="px-8 py-3 rounded-md text-base font-medium bg-[#E8941A] hover:bg-[#d68515] text-white transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
              data-testid="calculate-btn"
            >
              {t('common.next', language)} →
            </button>
          )}
        </div>
      )}
    </div>
  );
}
